import { useState } from "react";
import { Crown, X, LogIn, BookmarkPlus } from "lucide-react";

export default function GuestBanner({ user, onLogin, onUpgrade }) {
  const [dismissed, setDismissed] = useState(false);
  if (!user?.is_guest || dismissed) return null;

  const used = user.free_analyses_used ?? 0;
  const limit = user.free_analyses_limit ?? 3;
  const remaining = Math.max(0, limit - used);
  const exhausted = remaining === 0;

  return (
    <div
      data-testid="guest-banner"
      className="relative bg-[#0F0F13] border border-[#FF2D55]/20 rounded-2xl overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#FF2D55]/8 via-transparent to-[#39FF14]/4 pointer-events-none" />
      <div className="relative z-10 p-4 sm:p-5">
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          data-testid="guest-banner-dismiss"
          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-500 hover:text-white transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <div className="flex items-start gap-3 pr-8">
          <div className="w-10 h-10 rounded-xl bg-[#FF2D55]/10 border border-[#FF2D55]/20 flex items-center justify-center shrink-0">
            <BookmarkPlus className="w-5 h-5 text-[#FF2D55]" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF2D55] font-mono font-semibold mb-1">
              guest mode
            </div>
            <div className="font-display font-bold text-white text-sm sm:text-base leading-tight">
              {exhausted
                ? "You've used all 3 free analyses on this device"
                : `Save your history — ${remaining} free ${remaining === 1 ? "analysis" : "analyses"} left`}
            </div>
            <div className="text-[11px] text-zinc-400 font-body mt-1 leading-relaxed">
              Log in to sync across devices and keep your detections forever. Your free analyses carry over.
            </div>

            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={onLogin}
                data-testid="guest-banner-login"
                className="bg-[#FF2D55] text-white hover:bg-[#D11B47] font-bold px-4 py-2 rounded-full text-xs font-display tracking-tight transition-all flex items-center gap-1.5 shadow-[0_0_12px_rgba(255,45,85,0.3)]"
              >
                <LogIn className="w-3 h-3" strokeWidth={2.4} />
                Log in to save
              </button>
              {!exhausted && (
                <button
                  onClick={onUpgrade}
                  data-testid="guest-banner-upgrade"
                  className="bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white font-medium px-4 py-2 rounded-full text-xs font-display tracking-tight transition-colors flex items-center gap-1.5"
                >
                  <Crown className="w-3 h-3 text-[#FF2D55]" />
                  See unlimited
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
